import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, useAuth } from "../lib/api";
import {
  Send, MapPin, AtSign, ImagePlus, X, Trash2, Search, Loader2, Film, BookOpen,
  Mic, Square, Heart, MessageCircle, Repeat2, Quote as QuoteIcon, ChevronDown,
} from "lucide-react";

const MAX_TEXT = 2000;
const MAX_AUDIO_SEC = 60;

function authedUrl(itemId) {
  const token = localStorage.getItem("tk_token") || "";
  return `${API}/album/file/${itemId}?auth=${encodeURIComponent(token)}`;
}

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} g`;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

function avatarColor(seed) {
  const palette = ["#ff6b35", "#4992ff", "#16a34a", "#a855f7", "#ec4899", "#0ea5e9", "#f59e0b", "#14b8a6"];
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function canDeletePost(p, user) {
  if (user?.role === "admin") return true;
  if (p.author !== user?.username) return false;
  const ageH = (Date.now() - new Date(p.created_at).getTime()) / 3600000;
  return ageH <= 72;
}

export default function Gunluk() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [tagged, setTagged] = useState([]);
  const [location, setLocation] = useState(null);
  const [quotedPost, setQuotedPost] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showLocPicker, setShowLocPicker] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [filter, setFilter] = useState({ location: "", trip_day: 0 });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.location) params.location = filter.location;
      if (filter.trip_day) params.trip_day = filter.trip_day;
      const { data } = await api.get("/journal", { params });
      setPosts(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.location, filter.trip_day]);
  useEffect(() => {
    api.get("/users/public").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.username, u.display_name])), [users]);
  const avatarMap = useMemo(() => {
    const m = {};
    for (const u of users) if (u.avatar_url) m[u.username] = u.avatar_url;
    return m;
  }, [users]);

  const exportPdf = async () => {
    const token = localStorage.getItem("tk_token") || "";
    setExporting(true);
    try {
      const res = await fetch(`${API}/journal/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "mavi-yolculuk-gunluk-kitap.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message || "İndirilemedi"); } finally { setExporting(false); }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    if (fileRef.current) fileRef.current.value = "";
  };
  const uploadFiles = async (files) => {
    setUploadingMedia(true);
    const fresh = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/album/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        fresh.push(data);
      } catch (e) { /* skip */ }
    }
    setMedia((m) => [...m, ...fresh]);
    setUploadingMedia(false);
  };

  const uploadAudio = async (blob) => {
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : blob.type.includes("ogg") ? "ogg" : "m4a";
      fd.append("file", new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type || "audio/webm" }));
      fd.append("kind_hint", "audio");
      const { data } = await api.post("/album/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia((m) => [...m, data]);
    } catch (e) {
      alert(e.response?.data?.detail || "Ses yüklenemedi");
    } finally {
      setUploadingMedia(false);
    }
  };

  const removeMedia = (id) => setMedia((m) => m.filter((x) => x.id !== id));
  const toggleTag = (u) => setTagged((arr) => (arr.includes(u) ? arr.filter((x) => x !== u) : [...arr, u]));

  const submit = async () => {
    const t = text.trim();
    if (!t && media.length === 0 && !quotedPost) return;
    setPosting(true);
    try {
      const body = {
        text: t,
        tagged_people: tagged,
        media_ids: media.map((m) => m.id),
        location: location ? { name: location.name, lat: location.lat, lng: location.lng } : null,
        quoted_post_id: quotedPost?.id || null,
      };
      const { data } = await api.post("/journal", body);
      setPosts((p) => [data, ...p]);
      setText(""); setMedia([]); setTagged([]); setLocation(null); setQuotedPost(null);
    } catch (e) { alert(e.response?.data?.detail || "Paylaşılamadı"); } finally { setPosting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gönderi silinsin mi?")) return;
    try {
      await api.delete(`/journal/${id}`);
      setPosts((arr) => arr.filter((p) => p.id !== id));
    } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  const toggleLike = async (p) => {
    // optimistic
    const next = !p.liked_by_me;
    setPosts((arr) => arr.map((x) => x.id === p.id ? { ...x, liked_by_me: next, like_count: x.like_count + (next ? 1 : -1) } : x));
    try { await api.put(`/journal/${p.id}/like`); }
    catch { setPosts((arr) => arr.map((x) => x.id === p.id ? { ...x, liked_by_me: !next, like_count: x.like_count + (next ? -1 : 1) } : x)); }
  };

  const addComment = async (p, text) => {
    const t = (text || "").trim();
    if (!t) return null;
    try {
      const { data } = await api.post(`/journal/${p.id}/comment`, { text: t });
      setPosts((arr) => arr.map((x) => x.id === p.id ? { ...x, comments: [...(x.comments || []), data], comment_count: x.comment_count + 1 } : x));
      return data;
    } catch (e) { alert(e.response?.data?.detail || "Yorum eklenemedi"); return null; }
  };

  const removeComment = async (p, c) => {
    try {
      await api.delete(`/journal/${p.id}/comment/${c.id}`);
      setPosts((arr) => arr.map((x) => x.id === p.id ? { ...x, comments: x.comments.filter((y) => y.id !== c.id), comment_count: x.comment_count - 1 } : x));
    } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  const charsLeft = MAX_TEXT - text.length;
  const canPost = (text.trim().length > 0 || media.length > 0 || quotedPost) && !posting;

  return (
    <div className="space-y-4" data-testid="gunluk-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-display font-semibold">Seyahat Günlüğü</h2>
          <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Anılarını paylaş — yazı, ses, lokasyon, kişi etiketi ve medya ile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(filter.location || filter.trip_day) && (
            <button
              onClick={() => setFilter({ location: "", trip_day: 0 })}
              className="tk-btn-ghost flex items-center gap-1 text-xs"
              data-testid="gunluk-filter-clear"
            >
              <X className="w-3.5 h-3.5" /> Filtreyi Kaldır
            </button>
          )}
          {posts.length > 0 && (
            <button onClick={exportPdf} disabled={exporting} className="tk-btn-ghost flex items-center gap-1.5 disabled:opacity-50" data-testid="gunluk-export-pdf-btn" title="Günlük Kitap PDF olarak indir">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              Günlük Kitap
            </button>
          )}
        </div>
      </div>

      {(filter.location || filter.trip_day) && (
        <div className="tk-card p-2.5 px-3 text-sm flex items-center gap-2" style={{ backgroundColor: "#fff7ee" }} data-testid="gunluk-filter-active">
          <span style={{ color: "#7a4326" }}>Filtre:</span>
          {filter.location && <span className="tk-chip">📍 {filter.location}</span>}
          {filter.trip_day > 0 && <span className="tk-chip">📅 Gün {filter.trip_day}</span>}
        </div>
      )}

      <Compose
        user={user}
        avatarMap={avatarMap}
        text={text} setText={setText}
        media={media} removeMedia={removeMedia}
        tagged={tagged} setTagged={setTagged} toggleTag={toggleTag}
        location={location} setLocation={setLocation}
        quotedPost={quotedPost} setQuotedPost={setQuotedPost}
        userMap={userMap}
        uploadingMedia={uploadingMedia}
        onPickFiles={onPickFiles}
        uploadAudio={uploadAudio}
        fileRef={fileRef}
        onShowLoc={() => setShowLocPicker(true)}
        onShowTag={() => setShowTagPicker(true)}
        onSubmit={submit}
        posting={posting}
        canPost={canPost}
        charsLeft={charsLeft}
      />

      {loading ? (
        <div className="tk-card p-6 flex items-center justify-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      ) : posts.length === 0 ? (
        <div className="tk-card p-8 text-center text-sm" style={{ color: "hsl(var(--text-2))" }} data-testid="gunluk-empty">
          {filter.location || filter.trip_day ? "Bu filtreye uyan gönderi yok." : "Henüz hiç gönderi yok — ilk anıyı sen paylaş ✨"}
        </div>
      ) : (
        <div className="space-y-3" data-testid="gunluk-feed">
          {posts.map((p) => (
            <PostCard
              key={p.id} post={p} user={user}
              userMap={userMap} avatarMap={avatarMap}
              onDelete={() => remove(p.id)}
              onLike={() => toggleLike(p)}
              onComment={(t) => addComment(p, t)}
              onDeleteComment={(c) => removeComment(p, c)}
              onQuote={() => setQuotedPost(p)}
              onFilterLocation={(name) => setFilter((f) => ({ ...f, location: name }))}
              onFilterDay={(d) => setFilter((f) => ({ ...f, trip_day: d }))}
            />
          ))}
        </div>
      )}

      {showLocPicker && <LocationPicker onClose={() => setShowLocPicker(false)} onPick={(loc) => { setLocation(loc); setShowLocPicker(false); }} />}
      {showTagPicker && <TagPicker users={users} selected={tagged} onToggle={toggleTag} onClose={() => setShowTagPicker(false)} />}
    </div>
  );
}

function Compose({ user, avatarMap, text, setText, media, removeMedia, location, quotedPost, setQuotedPost, userMap, tagged, toggleTag, uploadingMedia, onPickFiles, uploadAudio, fileRef, onShowLoc, onShowTag, onSubmit, posting, canPost, charsLeft }) {
  return (
    <div className="tk-card p-4" data-testid="gunluk-compose">
      <div className="flex gap-3">
        <Avatar name={user?.display_name} username={user?.username} avatarUrl={avatarMap[user?.username]} />
        <div className="flex-1 min-w-0">
          <textarea
            className="w-full resize-none border-0 outline-none text-base leading-relaxed bg-transparent"
            rows={3}
            placeholder="Anını paylaş, günlüğümüze katkıda bulun..."
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
            data-testid="gunluk-compose-text"
          />
          {quotedPost && (
            <div className="rounded-xl border p-2.5 mb-2 flex items-start gap-2" style={{ borderColor: "hsl(var(--border))", backgroundColor: "hsl(var(--surface-2))" }} data-testid="gunluk-compose-quoted">
              <Avatar name={quotedPost.author_display} username={quotedPost.author} avatarUrl={avatarMap[quotedPost.author]} size={28} />
              <div className="flex-1 text-xs" style={{ color: "hsl(var(--text-2))" }}>
                <div><b className="text-[hsl(var(--text-1))]">{quotedPost.author_display}</b> · {fmtRel(quotedPost.created_at)}</div>
                <div className="line-clamp-2">{quotedPost.text}</div>
              </div>
              <button onClick={() => setQuotedPost(null)} className="p-1 rounded hover:bg-white/50" data-testid="gunluk-compose-quoted-remove"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5 mt-2" data-testid="gunluk-compose-media">
              {media.map((m) => (
                <div key={m.id} className="relative rounded-lg overflow-hidden" style={{ aspectRatio: m.kind === "audio" ? "3/1" : "1/1", backgroundColor: "hsl(var(--surface-2))" }}>
                  {m.kind === "photo" ? (
                    <img src={authedUrl(m.id)} alt="" className="w-full h-full object-cover" />
                  ) : m.kind === "audio" ? (
                    <div className="w-full h-full flex items-center justify-center gap-1.5 text-xs px-2" style={{ color: "#4992ff" }}>
                      <Mic className="w-4 h-4" /> Sesli not
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Film className="w-7 h-7" style={{ color: "hsl(var(--text-2))" }} /></div>
                  )}
                  <button onClick={() => removeMedia(m.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.65)", color: "white" }} data-testid={`gunluk-remove-media-${m.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(location || tagged.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {location && (
                <span className="tk-chip flex items-center gap-1 cursor-pointer" onClick={() => setText(text)} data-testid="gunluk-loc-chip" style={{ paddingRight: 4 }}>
                  <MapPin className="w-3 h-3" /> {location.name}
                </span>
              )}
              {tagged.map((u) => (
                <span key={u} className="tk-chip flex items-center gap-1 cursor-pointer" onClick={() => toggleTag(u)} data-testid={`gunluk-tag-chip-${u}`}>
                  <AtSign className="w-3 h-3" /> {userMap[u] || u} <X className="w-3 h-3 opacity-60" />
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "hsl(var(--border))" }}>
            <div className="flex items-center gap-1">
              <button onClick={() => fileRef.current?.click()} disabled={uploadingMedia} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-2))] transition disabled:opacity-50" title="Foto / Video" data-testid="gunluk-action-media">
                {uploadingMedia ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#4992ff" }} /> : <ImagePlus className="w-5 h-5" style={{ color: "#4992ff" }} />}
              </button>
              <AudioRecorder onComplete={uploadAudio} disabled={uploadingMedia} />
              <button onClick={onShowLoc} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-2))] transition" title="Konum ekle" data-testid="gunluk-action-location">
                <MapPin className="w-5 h-5" style={{ color: location ? "#ff6b35" : "#4992ff" }} />
              </button>
              <button onClick={onShowTag} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-2))] transition" title="Kişi etiketle" data-testid="gunluk-action-tag">
                <AtSign className="w-5 h-5" style={{ color: tagged.length > 0 ? "#ff6b35" : "#4992ff" }} />
              </button>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onPickFiles} className="hidden" data-testid="gunluk-file-input" />
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs ${charsLeft < 100 ? "text-[#dc2626]" : ""}`} style={{ color: charsLeft < 100 ? undefined : "hsl(var(--text-2))" }}>{charsLeft}</span>
              <button onClick={onSubmit} disabled={!canPost} className="tk-btn flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="gunluk-submit-btn">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Paylaş
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AudioRecorder({ onComplete, disabled }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const stopAt = useRef(null);

  useEffect(() => () => stop(true), []); // cleanup on unmount

  const start = async () => {
    if (disabled || recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Tarayıcınız ses kaydını desteklemiyor.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size > 0) onComplete?.(blob);
        setRecording(false);
        setSeconds(0);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      stopAt.current = Date.now() + MAX_AUDIO_SEC * 1000;
      tickRef.current = setInterval(() => {
        const left = Math.max(0, Math.ceil((stopAt.current - Date.now()) / 1000));
        setSeconds(MAX_AUDIO_SEC - left);
        if (left <= 0) stop(false);
      }, 200);
    } catch (e) {
      alert("Mikrofon erişimi reddedildi: " + e.message);
    }
  };

  const stop = (silent) => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* */ }
    }
    if (silent) setRecording(false);
  };

  if (recording) {
    return (
      <button onClick={() => stop(false)} className="px-2.5 py-2 rounded-lg flex items-center gap-1.5" style={{ backgroundColor: "#fee2e2", color: "#dc2626" }} data-testid="gunluk-audio-stop">
        <Square className="w-4 h-4 fill-current" />
        <span className="text-xs font-mono tabular-nums" data-testid="gunluk-audio-timer">{seconds}s / {MAX_AUDIO_SEC}s</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#dc2626] animate-pulse" />
      </button>
    );
  }
  return (
    <button onClick={start} disabled={disabled} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-2))] transition disabled:opacity-50" title="Sesli not (60 sn)" data-testid="gunluk-action-audio">
      <Mic className="w-5 h-5" style={{ color: "#4992ff" }} />
    </button>
  );
}

function Avatar({ name, username, avatarUrl, size = 40 }) {
  const initials = (name || username || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const src = avatarUrl ? (avatarUrl.startsWith("http") ? avatarUrl : `${backend}${avatarUrl}`) : null;
  if (src) {
    return <img src={src} alt={name || username} className="flex-shrink-0 object-cover" style={{ width: size, height: size, borderRadius: size / 2 }} data-testid={`avatar-${username}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  }
  return (
    <div className="flex-shrink-0 flex items-center justify-center font-semibold text-white" style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: avatarColor(username || name), fontSize: size * 0.4 }} data-testid={`avatar-${username}`}>
      {initials}
    </div>
  );
}

function PostCard({ post, user, userMap, avatarMap, onDelete, onLike, onComment, onDeleteComment, onQuote, onFilterLocation, onFilterDay }) {
  const canDel = canDeletePost(post, user);
  const media = post.media || [];
  const [showComments, setShowComments] = useState(false);
  const [reply, setReply] = useState("");
  const submitReply = async () => {
    const r = await onComment(reply);
    if (r) { setReply(""); setShowComments(true); }
  };
  return (
    <article className="tk-card p-4" data-testid={`gunluk-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={post.author_display} username={post.author} avatarUrl={avatarMap?.[post.author]} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-[15px]">{post.author_display}</span>
            <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>@{post.author}</span>
            <span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>· {fmtRel(post.created_at)}</span>
            {canDel && (
              <button onClick={onDelete} className="ml-auto p-1.5 rounded-lg hover:bg-[hsl(var(--surface-2))] transition" title="Sil" data-testid={`gunluk-delete-${post.id}`}>
                <Trash2 className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
              </button>
            )}
          </div>
          {post.text && <p className="mt-1 text-[15px] leading-relaxed whitespace-pre-wrap break-words">{post.text}</p>}
          {(post.location || (post.tagged_people || []).length > 0 || post.trip_day || post.weather) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {post.trip_day && (
                <button className="tk-chip flex items-center gap-1" onClick={() => onFilterDay?.(post.trip_day)} data-testid={`gunluk-post-day-${post.id}`} style={{ backgroundColor: "#fff7ee", color: "#7a4326", cursor: "pointer" }}>
                  📅 Gün {post.trip_day}
                </button>
              )}
              {post.location?.name && (
                <button className="tk-chip flex items-center gap-1" onClick={() => onFilterLocation?.(post.location.name)} data-testid={`gunluk-post-loc-${post.id}`} style={{ cursor: "pointer" }}>
                  <MapPin className="w-3 h-3" style={{ color: "#ff6b35" }} /> {post.location.name}
                </button>
              )}
              {post.weather && (
                <span className="tk-chip flex items-center gap-1" data-testid={`gunluk-post-weather-${post.id}`} title="Göcek hava durumu">
                  {post.weather.icon} {Math.round(post.weather.temp_c)}°C
                </span>
              )}
              {(post.tagged_people || []).map((u) => (
                <span key={u} className="tk-chip flex items-center gap-1" data-testid={`gunluk-post-tag-${post.id}-${u}`}>
                  <AtSign className="w-3 h-3" style={{ color: "#4992ff" }} /> {userMap[u] || u}
                </span>
              ))}
            </div>
          )}
          {post.quoted && (
            <div className="mt-3 rounded-xl border p-3 flex items-start gap-2" style={{ borderColor: "hsl(var(--border))" }} data-testid={`gunluk-post-quoted-${post.id}`}>
              <Avatar name={post.quoted.author_display} username={post.quoted.author} avatarUrl={avatarMap?.[post.quoted.author]} size={28} />
              <div className="flex-1 min-w-0 text-xs" style={{ color: "hsl(var(--text-2))" }}>
                <div><b className="text-[hsl(var(--text-1))]">{post.quoted.author_display}</b> · {fmtRel(post.quoted.created_at)}</div>
                <div className="text-[13px] mt-0.5" style={{ color: "hsl(var(--text-1))" }}>{post.quoted.text}</div>
                {post.quoted.media?.[0] && post.quoted.media[0].kind === "photo" && (
                  <img src={authedUrl(post.quoted.media[0].id)} alt="" className="mt-2 rounded-lg max-h-40 object-cover" />
                )}
              </div>
            </div>
          )}
          <PostMedia media={media} />
          {/* Action bar */}
          <div className="flex items-center gap-1 mt-3 -ml-1.5 text-sm" data-testid={`gunluk-actions-${post.id}`}>
            <button onClick={onLike} className="px-2 py-1.5 rounded-lg hover:bg-[#fee2e2] transition flex items-center gap-1.5" data-testid={`gunluk-like-${post.id}`}>
              <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-[#dc2626]" : ""}`} style={{ color: post.liked_by_me ? "#dc2626" : "hsl(var(--text-2))" }} />
              {post.like_count > 0 && <span style={{ color: post.liked_by_me ? "#dc2626" : "hsl(var(--text-2))" }}>{post.like_count}</span>}
            </button>
            <button onClick={() => setShowComments((s) => !s)} className="px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--surface-2))] transition flex items-center gap-1.5" data-testid={`gunluk-comment-toggle-${post.id}`}>
              <MessageCircle className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
              {post.comment_count > 0 && <span style={{ color: "hsl(var(--text-2))" }}>{post.comment_count}</span>}
            </button>
            <button onClick={onQuote} className="px-2 py-1.5 rounded-lg hover:bg-[#dbe9ff] transition flex items-center gap-1.5" title="Alıntıla" data-testid={`gunluk-quote-${post.id}`}>
              <QuoteIcon className="w-4 h-4" style={{ color: "hsl(var(--text-2))" }} />
            </button>
          </div>
          {showComments && (
            <div className="mt-2 pt-2 border-t space-y-2" style={{ borderColor: "hsl(var(--border))" }} data-testid={`gunluk-comments-${post.id}`}>
              {(post.comments || []).map((c) => {
                const canDelComment = user?.role === "admin" || c.author === user?.username || post.author === user?.username;
                return (
                  <div key={c.id} className="flex items-start gap-2" data-testid={`gunluk-comment-${c.id}`}>
                    <Avatar name={c.author_display} username={c.author} avatarUrl={avatarMap?.[c.author]} size={28} />
                    <div className="flex-1 min-w-0 rounded-lg px-3 py-1.5" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                      <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>
                        <b className="text-[hsl(var(--text-1))]">{c.author_display}</b> · {fmtRel(c.created_at)}
                      </div>
                      <div className="text-[13px] mt-0.5 whitespace-pre-wrap break-words">{c.text}</div>
                    </div>
                    {canDelComment && (
                      <button onClick={() => onDeleteComment(c)} className="p-1 rounded hover:bg-[hsl(var(--surface-2))]" data-testid={`gunluk-comment-delete-${c.id}`} title="Yorumu sil">
                        <X className="w-3.5 h-3.5" style={{ color: "hsl(var(--text-2))" }} />
                      </button>
                    )}
                  </div>
                );
              })}
              <div className="flex items-center gap-2">
                <Avatar name={user?.display_name} username={user?.username} avatarUrl={avatarMap?.[user?.username]} size={28} />
                <input
                  className="tk-input flex-1 !py-1.5 text-sm"
                  placeholder="Yorum yaz..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitReply(); } }}
                  maxLength={500}
                  data-testid={`gunluk-comment-input-${post.id}`}
                />
                <button onClick={submitReply} disabled={!reply.trim()} className="tk-btn !py-1.5 !px-2.5 text-sm disabled:opacity-50" data-testid={`gunluk-comment-submit-${post.id}`}>
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function PostMedia({ media }) {
  if (!media?.length) return null;
  const audios = media.filter((m) => m.kind === "audio");
  const visual = media.filter((m) => m.kind !== "audio");
  return (
    <>
      {visual.length > 0 && (
        <div className={`mt-3 grid gap-1 rounded-xl overflow-hidden ${visual.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {visual.slice(0, 4).map((m, i) => (
            <div key={m.id} className="relative" style={{ aspectRatio: visual.length === 1 ? "16/10" : "1/1", backgroundColor: "hsl(var(--surface-2))" }}>
              {m.kind === "photo" ? (
                <img src={authedUrl(m.id)} alt="" className="w-full h-full object-cover" />
              ) : (
                <video src={authedUrl(m.id)} controls className="w-full h-full object-cover" />
              )}
              {visual.length > 4 && i === 3 && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-xl font-semibold" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>+{visual.length - 4}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {audios.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {audios.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ backgroundColor: "#dbe9ff" }} data-testid={`audio-player-${m.id}`}>
              <Mic className="w-4 h-4 flex-shrink-0" style={{ color: "#4992ff" }} />
              <audio src={authedUrl(m.id)} controls className="flex-1 h-8" />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function LocationPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&accept-language=tr&q=${encodeURIComponent(q)}&limit=8`, { headers: { "Accept-Language": "tr" } });
      const j = await r.json();
      setResults(j || []);
    } catch { setResults([]); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="gunluk-loc-modal">
      <div className="tk-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold flex items-center gap-2"><MapPin className="w-5 h-5" style={{ color: "#ff6b35" }} /> Konum Seç</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1.5 mb-3">
          <input className="tk-input flex-1" placeholder="örn. Tersane Adası, Göcek" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} data-testid="gunluk-loc-search-input" autoFocus />
          <button onClick={search} disabled={busy} className="tk-btn !py-1.5 !px-3 text-sm disabled:opacity-50" data-testid="gunluk-loc-search-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </div>
        {results.length > 0 && (
          <ul className="border rounded-md divide-y max-h-64 overflow-y-auto" style={{ borderColor: "hsl(var(--border))" }} data-testid="gunluk-loc-results">
            {results.map((r) => (
              <li key={r.place_id}>
                <button type="button" onClick={() => onPick({ name: (r.display_name || "").split(",").slice(0, 2).join(",").trim(), lat: parseFloat(r.lat), lng: parseFloat(r.lon) })} className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--surface-2))] text-sm" data-testid={`gunluk-loc-result-${r.place_id}`}>
                  <div className="font-medium">{(r.display_name || "").split(",").slice(0, 2).join(",")}</div>
                  <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>{r.display_name}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TagPicker({ users, selected, onToggle, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="gunluk-tag-modal">
      <div className="tk-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold flex items-center gap-2"><AtSign className="w-5 h-5" style={{ color: "#4992ff" }} /> Kişi Etiketle</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {users.map((u) => {
            const active = selected.includes(u.username);
            return (
              <button key={u.username} onClick={() => onToggle(u.username)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition ${active ? "bg-[#dbe9ff]" : "hover:bg-[hsl(var(--surface-2))]"}`} data-testid={`gunluk-tag-pick-${u.username}`}>
                <Avatar name={u.display_name} username={u.username} avatarUrl={u.avatar_url} size={36} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{u.display_name}</div>
                  <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>@{u.username}</div>
                </div>
                {active && <span className="text-xs font-semibold text-[#4992ff]">Seçili</span>}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={onClose} className="tk-btn text-sm">Tamam</button>
        </div>
      </div>
    </div>
  );
}
